import { ClearOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Card, Col, Empty, Input, message, Radio, Row, Select, Space, Table, Tag, Typography } from 'antd';
import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import api from '~/services/api';
import history from '~/services/history';

import { Container } from './styles';
import TransferToSalesOrderModal from './TransferToSalesOrderModal';

export default function InternalOrders() {
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [orders, setOrders] = useState([]);
  const [requestedByOptions, setRequestedByOptions] = useState([]);
  const [createdByOptions, setCreatedByOptions] = useState([]);
  const [technicianOptions, setTechnicianOptions] = useState([]);
  const [transferModalOpen, setTransferModalOpen] = useState(false);
  // selectionMode: 'complete' | 'transfer'
  const [selectionMode, setSelectionMode] = useState('complete');

  const [filters, setFilters] = useState({
    status: [],
    filterType: 'orderNumber', // 'orderNumber' or 'serialNumber'
    filterValue: '',
    requestedById: null,
    createdById: null,
    technicianId: null,
  });

  const { Paragraph, Title } = Typography;

  const tagColor = {
    in_progress: {
      label: 'In Progress',
      color: 'orange',
    },
    cancelled: {
      label: 'Cancelled',
      color: 'red',
    },
    complete: {
      label: 'Complete',
      color: 'green',
    },
    pending: {
      label: 'Pending',
      color: 'blue',
    },
  };

  const handleSearch = useCallback(async () => {
    const params = new URLSearchParams();

    // Add filterType and filterValue to the API call if filterValue is present
    if (filters.filterValue) {
      params.append('filterType', filters.filterType);
      params.append('filterValue', filters.filterValue);
    }
    if (filters.status.length > 0) {
      params.append('status', filters.status.join(','));
    }
    if (filters.requestedById) {
      params.append('requested_by_id', filters.requestedById);
    }
    if (filters.createdById) {
      params.append('created_by_id', filters.createdById);
    }
    if (filters.technicianId) {
      params.append('technician_id', filters.technicianId);
    }

    const { data: ordersData } = await api.get(`internal_orders?${params.toString()}`);

    if (Array.isArray(ordersData)) {
      setOrders(ordersData);
      setRequestedByOptions([]);
      setCreatedByOptions([]);
      setTechnicianOptions([]);
    } else {
      setOrders(ordersData.data || []);
      setRequestedByOptions(ordersData.requestedBy || []);
      setCreatedByOptions(ordersData.createdBy || []);
      setTechnicianOptions(ordersData.technicians || []);
    }
  }, [filters]);

  const handleClearFilters = async () => {
    setFilters({
      status: [],
      filterType: 'orderNumber',
      filterValue: '',
      requestedById: null,
      createdById: null,
      technicianId: null,
    });

    const { data: ordersData } = await api.get('internal_orders');

    if (Array.isArray(ordersData)) {
      setOrders(ordersData);
      setRequestedByOptions([]);
      setCreatedByOptions([]);
      setTechnicianOptions([]);
    } else {
      setOrders(ordersData.data || []);
      setRequestedByOptions(ordersData.requestedBy || []);
      setCreatedByOptions(ordersData.createdBy || []);
      setTechnicianOptions(ordersData.technicians || []);
    }
  };

  useEffect(() => {
    handleSearch();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const columns = [
    {
      title: 'Internal Order Number',
      dataIndex: 'internal_order_number',
      render: (text, record) => (
        <Link to={`/InternalOrderEdit?internal_order_id=${record.id}`}>
          <Paragraph style={{ color: '#1890ff' }} copyable>
            {text}
          </Paragraph>
        </Link>
      ),
    },
    {
      title: 'Created By',
      dataIndex: ['created_by', 'name'],
    },
    {
      title: 'Requested By',
      dataIndex: ['requested_by', 'name'],
    },
    {
      title: 'Technician',
      dataIndex: ['technician', 'name'],
    },
    {
      title: 'Order Date',
      dataIndex: 'creation_date',
      render: (text) => moment(text).format('MMM DD, YYYY'),
    },
    {
      title: 'Notes',
      dataIndex: 'notes',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      render: (text) => (
        <Tag color={tagColor[text]?.color} key={tagColor[text]?.label}>
          {tagColor[text]?.label.toUpperCase() || text}
        </Tag>
      ),
    },
  ];

  const handleCompleteMany = async () => {
    setCompleting(true);
    try {
      const { data } = await api.post('internal_orders/complete-many', { ids: selectedRowKeys });
      message.success(`Successfully completed ${data.updated} internal order(s).`);
      setSelectedRowKeys([]);
      handleSearch();
    } catch (err) {
      message.error('Failed to complete selected internal orders.');
    } finally {
      setCompleting(false);
    }
  };

  // Update rowSelection logic based on selectionMode
  const rowSelection = {
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys),
    getCheckboxProps: (record) => {
      if (selectionMode === 'complete') {
        // Only allow selecting orders that are not completed
        return { disabled: record.status !== 'in_progress' };
      }
      if (selectionMode === 'transfer') {
        // Only allow selecting completed orders
        return { disabled: record.status !== 'complete' };
      }
      return {};
    },
  };

  return (
    <Container>
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col flex="auto">
            <Title level={3} style={{ margin: 0 }}>
              Internal Orders
            </Title>
          </Col>
          <Col>
            <Space>
              <Button onClick={() => history.push('/orders')}>Back to Orders</Button>
              <Button onClick={() => history.push('/internalOrderNew')} type="primary">
                New Order
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card size="small" style={{ marginBottom: '16px' }}>
        <Row gutter={[16, 16]}>
          <Col xs={24} sm={12} md={10} lg={8}>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Search By</div>
            <Input.Group compact>
              <Select
                value={filters.filterType}
                style={{ width: 140 }}
                onChange={(value) => setFilters({ ...filters, filterType: value, filterValue: '' })}
                options={[
                  { label: 'Order Number', value: 'orderNumber' },
                  { label: 'Serial Number', value: 'serialNumber' },
                ]}
              />
              <Input
                style={{ width: 'calc(100% - 140px)' }}
                placeholder={filters.filterType === 'serialNumber' ? 'Enter serial number' : 'Enter order number'}
                value={filters.filterValue}
                onChange={(e) => setFilters({ ...filters, filterValue: e.target.value })}
                onPressEnter={handleSearch}
                allowClear
              />
            </Input.Group>
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Status</div>
            <Select
              mode="multiple"
              placeholder="Select status"
              style={{ width: '100%' }}
              value={filters.status}
              onChange={(value) => setFilters({ ...filters, status: value })}
              options={[
                { label: 'Pending', value: 'pending' },
                { label: 'In Progress', value: 'in_progress' },
                { label: 'Complete', value: 'complete' },
              ]}
              allowClear
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Requested By</div>
            <Select
              placeholder="Select requester"
              style={{ width: '100%' }}
              value={filters.requestedById}
              onChange={(value) => setFilters({ ...filters, requestedById: value })}
              options={requestedByOptions.map((item) => ({ label: item.name, value: item.id }))}
              allowClear
              disabled={requestedByOptions.length === 0}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Created By</div>
            <Select
              placeholder="Select creator"
              style={{ width: '100%' }}
              value={filters.createdById}
              onChange={(value) => setFilters({ ...filters, createdById: value })}
              options={createdByOptions.map((item) => ({ label: item.name, value: item.id }))}
              allowClear
              disabled={createdByOptions.length === 0}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6}>
            <div style={{ display: 'block', marginBottom: '4px', fontWeight: 500 }}>Technician</div>
            <Select
              placeholder="Select technician"
              style={{ width: '100%' }}
              value={filters.technicianId}
              onChange={(value) => setFilters({ ...filters, technicianId: value })}
              options={technicianOptions.map((item) => ({ label: item.name, value: item.id }))}
              allowClear
              disabled={technicianOptions.length === 0}
            />
          </Col>

          <Col xs={24} sm={12} md={8} lg={6} style={{ display: 'flex', alignItems: 'flex-end' }}>
            <Space>
              <Button type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
                Search
              </Button>
              <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
                Clear Filters
              </Button>
            </Space>
          </Col>
        </Row>
      </Card>

      <Card size="small">
        <Space style={{ marginBottom: 16, width: '100%', justifyContent: 'space-between' }}>
          <Radio.Group
            value={selectionMode}
            onChange={(e) => {
              setSelectionMode(e.target.value);
              setSelectedRowKeys([]); // Clear selection on mode change
            }}
            optionType="button"
            buttonStyle="solid"
          >
            <Radio.Button value="complete">Complete Orders</Radio.Button>
            <Radio.Button value="transfer">Transfer to Sales Order</Radio.Button>
          </Radio.Group>
          <Space>
            {selectionMode === 'complete' && (
              <Button type="primary" disabled={selectedRowKeys.length === 0} loading={completing} onClick={handleCompleteMany}>
                Complete Selected
              </Button>
            )}
            {selectionMode === 'transfer' && (
              <Button type="primary" disabled={selectedRowKeys.length === 0} onClick={() => setTransferModalOpen(true)}>
                Transfer to Sales Order
              </Button>
            )}
          </Space>
        </Space>
        {orders.length > 0 ? (
          <Table
            columns={columns}
            dataSource={orders}
            size="small"
            bordered
            pagination={false}
            scroll={{ y: 'calc(100vh - 28em)' }}
            rowKey={(record) => record.id}
            rowSelection={rowSelection}
          />
        ) : (
          <Empty
            description={<span>No orders found</span>}
            style={{
              padding: '60px 0',
            }}
          />
        )}
      </Card>
      {/* Transfer to Sales Order Modal */}
      <TransferToSalesOrderModal
        open={transferModalOpen}
        onClose={() => setTransferModalOpen(false)}
        selectedInternalOrderIds={selectedRowKeys}
        onTransferSuccess={() => {
          setTransferModalOpen(false);
          setSelectedRowKeys([]);
          handleSearch();
        }}
      />
    </Container>
  );
}
